import type { TFunction } from 'i18next'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'

import { PageOption, maxNestedPageLevel, treeLines } from './PageOption'
import { isStartpage } from '../PageEditPanel'
import { useTranslate } from '../../../../utils/translate'
import CustomSelect from '../../../templateComponents/Workspace/plugins/components/CustomSelect'
import FormGroup from '../../../formComponents/FormGroup'
import FormValue from '../../../formComponents/FormValue'

const getBranchDrawPattern = (
  navigationTreeList,
  currentNodeLevel,
  node,
  previousNodeLevel = 0,
  previousDrawSymbols = [],
) => {
  const branchDrawSymbols: string[] = []

  if (currentNodeLevel > 0) {
    const isLastAtBranchLevel = navigationTreeList.indexOf(node) + 1 === navigationTreeList.size

    // checking the previous way of drawing
    if (previousDrawSymbols.length > 0) {
      if (currentNodeLevel > previousNodeLevel) {
        branchDrawSymbols.push(...previousDrawSymbols.slice(0, -1))

        if (previousDrawSymbols[previousDrawSymbols.length - 1] === treeLines.lineDownRight) {
          branchDrawSymbols.push(treeLines.lineDown)
        } else {
          branchDrawSymbols.push(treeLines.space)
        }
      } else {
        if (currentNodeLevel - previousNodeLevel) {
          branchDrawSymbols.push(...previousDrawSymbols.slice(0, currentNodeLevel - 1))
        } else {
          branchDrawSymbols.push(...previousDrawSymbols.slice(0, -1))
        }
      }
    }

    // symbol right before name, should be the last
    branchDrawSymbols.push(isLastAtBranchLevel ? treeLines.lineRight : treeLines.lineDownRight)
  }

  return branchDrawSymbols
}

const getLevelList = (navigationTreeList: any, currentNodeLevel = 0, levelList: any = Immutable.List()) => {
  navigationTreeList.forEach((node) => {
    levelList = levelList.push(
      Immutable.Map({
        id: node.get('id'),
        href: node.get('href'),
        isVisible: node.get('isVisible'),
        title: node.get('title'),
        slug: node.get('slug'),
        level: currentNodeLevel,
        branchDrawPattern: getBranchDrawPattern(
          navigationTreeList,
          currentNodeLevel,
          node,
          levelList.last()?.get('level'),
          levelList.last()?.get('branchDrawPattern'),
        ),
        parents: node.get('parents'),
      }),
    )

    if (node.get('children').size > 0) {
      levelList = getLevelList(node.get('children'), currentNodeLevel + 1, levelList)
    }
  })

  return levelList
}

const getRootPageNode = (t: TFunction) =>
  Immutable.Map({
    id: 'main-menu',
    isVisible: true,
    title: t(
      'components.editorSidebarComponent.pageManagerSection.contentPagesSection.pageSettingsModal.pageLevelSection.mainLevel',
    ),
    slug: '',
    level: 0,
    children: Immutable.List(),
    parents: Immutable.List(),
  })

const isValidPageLevel = (page) => page.get('level') + 1 < maxNestedPageLevel

const getSelectedPageIndex = (pageList, pageSlug) => {
  return pageList.indexOf(pageList.find((nav) => nav.get('slug') === pageSlug))
}

export default function SelectPageLevelPanel() {
  const t = useTranslate('interface') as TFunction

  const editorNavigation = useSelector<State, ImmutableList>(
    (state) =>
      state.getIn(['navigation', 'editor']).filterNot((nav) => nav.get('type') && nav.get('type') === 'category'), // condition for Now shops
  )

  const pageLevelList = Immutable.List([getRootPageNode(t)])
    .concat(getLevelList(editorNavigation.filterNot(isStartpage)).filter(isValidPageLevel))
    .toList()

  return (
    <FormGroup
      name="pageParentSlug"
      tagName="label"
      label={t(
        'components.editorSidebarComponent.pageManagerSection.contentPagesSection.pageSettingsModal.pageLevelSection.label',
      )}
    >
      <FormValue name="pageParentSlug" key="pageParentSlug">
        {(pageParentSlug: string, setPageParentSlug: (data: string) => void) => {
          const selectedIndex = getSelectedPageIndex(pageLevelList, pageParentSlug)

          return (
            <CustomSelect
              className="ep-page-level-select"
              name="selectParent"
              selectedIndex={selectedIndex}
              options={pageLevelList}
              onChange={(index) =>
                pageLevelList.get(index) && setPageParentSlug(pageLevelList.get(index).get('slug') as string)
              }
              renderOption={(page) => (
                <PageOption {...{ page }} isSelected={pageLevelList.get(selectedIndex).get('id') === page.get('id')} />
              )}
              renderSelectedOption={(selectedPage) =>
                selectedPage && <label className={'ep-custom-select-label'}>{selectedPage.get('title')}</label>
              }
              showSelectedInList
            />
          )
        }}
      </FormValue>
    </FormGroup>
  )
}
